<script setup lang="ts">
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '~ds/components/ui/breadcrumb';
import { Heading } from '~ds/components/ui/typography';
import { parseVideoUrl, videoEmbedUrl } from '~kui/utils/videoUrl';

import CoachSubpageNotFound from '~/components/Coach/CoachSubpageNotFound.vue';
import VideoDescription from '~/components/Unified/Video/VideoDescription.vue';
import VideoShareButton from '~/components/Unified/Video/VideoShareButton.vue';
import VideoSpeakers from '~/components/Unified/Video/VideoSpeakers.vue';
import VideoSponsorRow from '~/components/Unified/Video/VideoSponsorRow.vue';
import { useIaLayout } from '~/composables/unified/useIaBackFrame';
import { useIaPlaces } from '~/composables/unified/useIaPlaces';
import { usePageLookupFailure } from '~/composables/unified/usePageLookupFailure';
import { useSignupCta } from '~/composables/unified/useSignupCta';
import { useVideoDetail } from '~/composables/unified/useVideoDetail';
import { UNIFIED_ROUTE_VIDEO_DETAIL } from '~/constants/routes/unified';
import { SEARCH_FETCH_INDUSTRY_HUB_BY_SLUG } from '~/graphql/documents/search';
import { EntrySource } from '~/graphql/generated/graphql';
import { buildVideoSchema, stripBrandSuffix, truncateMetaDescription } from '~/utils/seo';

/**
 * A video's own page: the player, its sponsor, a share button, the description and the speakers,
 * with a crumb back to the hub a visitor arrived from (`?hub=`). The public `/videos/:slug` and the
 * coach shell's `/coach/videos/:slug` both render it.
 */
const route = useRoute();
const { $analytics } = useNuxtApp();
const places = useIaPlaces();
const slug = route.params.slug as string;
const hubSlug = typeof route.query.hub === 'string' && route.query.hub !== '' ? route.query.hub : null;

const { video, error } = await useVideoDetail(slug);

const { failed } = usePageLookupFailure(error);

// The crumb is a courtesy for visitors arriving from a hub, so an unrecognised `?hub=` (or a
// failed lookup) drops the breadcrumb rather than failing the video; `useIndustryHub` would
// hard-404 an unknown slug server-side, so the index is read directly for just the title.
const { $urqlClient } = useNuxtApp();
const { data: hub } = await useAsyncData(
  `video-hub-crumb-${hubSlug ?? 'none'}`,
  async () => {
    if (!hubSlug) {
      return null;
    }
    const result = await $urqlClient.query(SEARCH_FETCH_INDUSTRY_HUB_BY_SLUG, { slug: hubSlug }, { requestPolicy: 'cache-first' }).toPromise();
    const found = result.data?.search?.fetchIndustryHubBySlug;
    return found ? { slug: found.slug, title: found.title } : null;
  },
  { server: true },
);

const content = computed(() => video.value?.publishedContent?.data ?? null);
const thumbnailUrl = computed(() => video.value?.coverImage?.publicUrl ?? video.value?.coverImage?.url ?? null);

const cluster = useCurrentCluster();
const pageUrl = computed(() => `${cluster.baseUrl.value}${UNIFIED_ROUTE_VIDEO_DETAIL({ slug })}`);

const title = computed(() => stripBrandSuffix(video.value?.meta?.title) || video.value?.name);
const metaDescription = computed(() => {
  const description = video.value?.meta?.description || content.value?.description;
  return description ? truncateMetaDescription(description) : undefined;
});

useSeoMeta({
  title: () => title.value,
  description: () => metaDescription.value,
  ogTitle: () => title.value,
  ogDescription: () => metaDescription.value,
  ogImage: () => thumbnailUrl.value ?? undefined,
  ogType: 'video.other',
});

// Guarded on the resolved video (a missing slug hard-404s server-side before this; SPA
// navigations don't need head schema).
if (video.value && content.value) {
  const parsed = parseVideoUrl(content.value.videoUrl);
  useSchemaOrg([buildVideoSchema({
    url: pageUrl.value,
    name: title.value ?? '',
    description: metaDescription.value,
    thumbnailUrl: thumbnailUrl.value ?? undefined,
    uploadDate: video.value.publishedAt ?? undefined,
    embedUrl: parsed ? videoEmbedUrl(parsed) : undefined,
  })]);
}

const handlePlay = () => {
  $analytics.track(EVENTS.UNIFIED_VIDEO_PLAYED, { videoSlug: slug, provider: content.value?.provider ?? null, hub: hubSlug });
};

const analytics = useAnalyticsService();
onMounted(() => {
  analytics.track(EVENTS.UNIFIED_PUBLIC_PAGE_VIEWED, { page: 'video', videoSlug: slug });
});

const { showSignUpCta, signUpLink, trackSignUpClick } = useSignupCta({
  entrySource: EntrySource.VideoPage,
  page: 'video',
  trackProps: { videoSlug: slug },
});

const iaLayout = useIaLayout();
</script>

<template>
  <div class="flex-1">
    <CoachSubpageNotFound v-if="failed" />
    <!-- Gated on the video, not `fetching`: a client-side navigation lands here while the lazy
         fetch is still idle, and an unknown slug is the error page (or, in the shell, the
         not-found above) via usePageLookupFailure. -->
    <KUICoreContainerCentered v-else-if="!video || !content" class="py-32">
      <KUICoreLoaderDot />
    </KUICoreContainerCentered>

    <div
      v-else
      class="m-auto flex flex-col gap-8 pb-24"
      :class="iaLayout ? 'max-w-7xl px-4' : 'max-w-5xl pt-8'"
      data-testid="video-page"
    >
      <Breadcrumb v-if="hub">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink as-child>
              <NuxtLink :to="places.hub(hub.slug)">
                {{ hub.title }}
              </NuxtLink>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{{ video.name }}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <KUICoreVideoEmbed
        :url="content.videoUrl"
        :title="video.name"
        :thumbnail-url="thumbnailUrl"
        @play="handlePlay"
      />

      <div class="flex flex-col gap-4">
        <Heading as="h1" size="xl">
          {{ video.name }}
        </Heading>
        <div class="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
          <VideoSponsorRow
            v-if="video.sponsor"
            :name="video.sponsor.name"
            :logo-url="video.sponsor.logoUrl"
          />
          <VideoShareButton
            :title="video.name"
            :url="pageUrl"
            class="ml-auto"
          />
        </div>
        <VideoDescription :text="content.description" />
      </div>

      <VideoSpeakers :speakers="content.speakers" />

      <div v-if="showSignUpCta" class="flex justify-center">
        <KUIUnifiedSignUpCtaCard
          :to="signUpLink"
          :title="$t('unified.signUpCta.video.title')"
          :button-label="$t('unified.signUpCta.video.button')"
          is-blinky
          @click="trackSignUpClick"
        />
      </div>
    </div>
  </div>
</template>
